import {
  useMotionTemplate,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { prefixMotionStyle } from 'src/shared/utils/styles';

import type { IsScrollableOptions } from './useIsScrollable';
import { useIsScrollable } from './useIsScrollable';

type PixelDistance = `${number}px`;
type PercentageDistance = `${number}%`;

export type Distance = PixelDistance | PercentageDistance;

const isPixels = (distance: Distance): distance is PixelDistance =>
  distance.endsWith('px');

export interface ScrollFadeOptions
  extends Pick<IsScrollableOptions, 'direction' | 'ref'> {
  /**
   * Distance in either pixels or percentage of the fade effect from the edge of
   * the container when fully applied
   *
   * @example '20%' or '15px'
   */
  distance?: Distance;
  /**
   * Scroll distance in pixels or percent at which the fade effect
   * should be fully applied. For example, with 25% the fade effect will be
   * the full `distance` at the start of the conatiner when the container is
   * scrolled 25% of the available scroll distance.
   *
   * @example '25%' or '50px'
   */
  threshold?: Distance;
  padding?: Distance;
  /**
   * Whether the fade affect will be applied initially while we're determining
   * whether the container is scrollable or not. This is mostly for SSR, but if
   * you know the container will always be scrollable set this to `true`.
   *
   * @default false
   */
  initialFade?: boolean;
}

/**
 * Apply a mask-based fade effect to the start and end of an element
 * depending on whether scroll content is overflowing on either side.
 *
 * @param options options
 *
 * @returns style object to be passed or spread into a Framer `motion`
 * component's `style` prop
 */
const useScrollFade = ({
  ref,
  direction,
  threshold = '25%',
  distance = '20%',
  padding = '0%',
  initialFade = false,
}: ScrollFadeOptions) => {
  const { scrollYProgress, scrollXProgress, scrollX, scrollY } = useScroll({
    container: ref,
  });

  const [isFadingStart, setIsFadingStart] = useState(false);
  const [isFadingEnd, setIsFadingEnd] = useState(false);

  const [isScrollable, size] = useIsScrollable({
    ref,
    direction,
    initial: initialFade,
  });

  const { scrollHeight = 0, scrollWidth = 0, width, height } = size ?? {};

  // This determines which scroll value to track and how to transform the fade
  // gradients based on whether the threshold is a percentage or pixels
  const { scroll, startTransform, endTransform } = useMemo(() => {
    const isThresholdPixels = isPixels(threshold);
    const thresholdInteger = parseInt(threshold, 10);
    const thresholdNumber = isThresholdPixels
      ? thresholdInteger
      : thresholdInteger / 100;

    const scrollProgess =
      direction === 'horizontal' ? scrollXProgress : scrollYProgress;
    const scrollDistance = direction === 'horizontal' ? scrollX : scrollY;

    // If the threshold passed is a percentage, we don't need to do any of the
    // calculations below that require the element size and scroll size
    if (!isThresholdPixels) {
      return {
        scroll: scrollProgess,
        startTransform: [0, thresholdNumber],
        endTransform: [1 - thresholdNumber, 1],
      };
    }

    const elementSize = direction === 'horizontal' ? width : height;

    // Fall back to percentage based fade until we have all the measurements we need
    if (!elementSize) {
      return {
        scroll: scrollProgess,
        startTransform: [0, 1],
        endTransform: [0, 1],
      };
    }

    const scrollSize = direction === 'horizontal' ? scrollWidth : scrollHeight;
    const scrollMax = scrollSize - elementSize;

    // Use percentage based fade if the supplied threshold is greater than the
    // scroll distance as in this case the fade would never be fully applied
    if (thresholdNumber > scrollMax) {
      return {
        scroll: scrollProgess,
        startTransform: [0, 1],
        endTransform: [0, 1],
      };
    }

    return {
      scroll: scrollDistance,
      startTransform: [0, thresholdNumber],
      endTransform: [scrollMax - thresholdNumber, scrollMax],
    };
  }, [
    threshold,
    direction,
    scrollXProgress,
    scrollYProgress,
    scrollX,
    scrollY,
    width,
    height,
    scrollWidth,
    scrollHeight,
  ]);

  const zero = isPixels(distance) ? '0px' : '0%';

  const startDistance = useTransform(scroll, startTransform, [zero, distance]);
  const endDistance = useTransform(scroll, endTransform, [distance, zero]);

  const [, startThreshold] = startTransform;
  const [endThreshold] = endTransform;

  const checkFades = useCallback(
    (v: number) => {
      if (!isScrollable) {
        setIsFadingStart(false);
        setIsFadingEnd(false);
        return;
      }

      if (v >= startThreshold && !isFadingStart) {
        setIsFadingStart(true);
      } else if (v < startThreshold && isFadingStart) {
        setIsFadingStart(false);
      }

      if (v <= endThreshold && !isFadingEnd) {
        setIsFadingEnd(true);
      } else if (v > endThreshold && isFadingEnd) {
        setIsFadingEnd(false);
      }
    },
    [endThreshold, isFadingEnd, isFadingStart, isScrollable, startThreshold],
  );

  useMotionValueEvent(scroll, 'change', v => checkFades(v));

  useEffect(() => checkFades(scroll.get()));

  const startFade = isScrollable ? startDistance : zero;
  const endFade = isScrollable ? endDistance : zero;

  const startDirection = direction === 'horizontal' ? 'right' : 'bottom';
  const endDirection = direction === 'horizontal' ? 'left' : 'top';

  const maskImage = useMotionTemplate`
    linear-gradient(to ${startDirection}, transparent ${padding}, black ${startFade}, black 51%, transparent 51%),
    linear-gradient(to ${endDirection}, transparent ${padding}, black ${endFade}, black 51%, transparent 51%)
  `;

  return {
    style: prefixMotionStyle({ maskImage }),
    isFadingStart,
    isFadingEnd,
  };
};

export { useScrollFade };
